import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Markdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Loader, Trash2, X } from 'lucide-react';
import config from '../../config';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const CHAT_STORAGE_KEY = 'ai_hybrid_chat_v2';

const getInitialMessage = () => ({
  id: 1,
  role: 'assistant',
  content: `Hello! I'm your intelligent warranty assistant. I can answer questions about:\n\n- **Database queries** — claim counts, statuses, amounts, vehicles\n- **Document search** — warranty policies, audio exchange guidelines, compliance rules`,
  timestamp: Date.now(),
  meta: null,
});

// Convert PDF filenames in the **Sources:** section into markdown download links.
const addSourceLinks = (text) => {
  const idx = text.indexOf('**Sources:**');
  if (idx === -1) return text;

  const before      = text.slice(0, idx);
  const sourcesText = text.slice(idx);

  const seen = new Set();
  const normalise = (s) => s.toLowerCase().replace(/[''‚‛']/g, "'").trim();
  const filenames = [...sourcesText.matchAll(/^[-*•]?\s*([A-Za-z][^\[\]\n]*\.pdf)/gim)]
    .map(m => m[1].trim())
    .filter(f => {
      const key = normalise(f);
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });

  if (!filenames.length) return text;

  const linkLines = filenames.map(f => {
    const url = `${config.API_BASE_URL}/download-doc?filename=${encodeURIComponent(f)}`;
    return `- [${f}](${url})`;
  });

  return `${before}**Sources:**\n\n${linkLines.join('\n')}`;
};

const formatTime = (ts) => {
  try {
    return new Date(ts).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  } catch (_) {
    return '';
  }
};

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

const ChatSection = ({ onClose }) => {
  const [messages, setMessages] = useState(() => {
    try {
      const saved = localStorage.getItem(CHAT_STORAGE_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        // Always refresh the welcome message timestamp so it shows current time
        if (parsed[0]?.id === 1) parsed[0].timestamp = Date.now();
        return parsed;
      }
    } catch (_) {}
    return [getInitialMessage()];
  });

  const [input, setInput]         = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const scrollRef                 = useRef(null);

  useEffect(() => {
    localStorage.setItem(CHAT_STORAGE_KEY, JSON.stringify(messages));
  }, [messages]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, isLoading]);

  const buildHistory = (msgs) =>
    msgs
      .filter((m) => m.id !== 1)
      .map((m) => `${m.role === 'user' ? 'User' : 'Assistant'}: ${m.content}`)
      .join('\n\n');

  const callChatAPI = async (userMessage, priorMessages) => {
    const history = buildHistory(priorMessages.slice(0, -1));
    const response = await fetch(`${config.API_BASE_URL}/chat`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ message: userMessage, history: history || null }),
    });
    if (!response.ok) throw new Error(`API Error: ${response.status}`);
    return response.json();
  };

  const handleSend = async () => {
    if (!input.trim() || isLoading) return;

    const now    = Date.now();
    const userMsg = { id: now, role: 'user', content: input.trim(), timestamp: now, meta: null };
    const updated = [...messages, userMsg];
    setMessages(updated);
    setInput('');
    setIsLoading(true);

    try {
      const data      = await callChatAPI(userMsg.content, updated);
      const rawAnswer = addSourceLinks(
        (data.answer || 'No response received.')
          .replace(/\n*(\*\*Sources:\*\*)/g, '\n\n$1')
      );

      const aiMsg = {
        id:        Date.now() + 1,
        role:      'assistant',
        content:   rawAnswer,
        timestamp: Date.now() + 1,
        meta: {
          intent:         data.intent        || 'GENERAL',
          sql_query:      data.sql_query     || null,
          sources:        data.sources       || [],
          documents_used: data.documents_used || [],
          success:        data.success       ?? true,
          error:          data.error         || null,
        },
      };
      setMessages((prev) => [...prev, aiMsg]);
    } catch (err) {
      console.error('[ChatSection] API error:', err);
      setMessages((prev) => [
        ...prev,
        {
          id:        Date.now() + 1,
          role:      'assistant',
          content:   'Sorry, something went wrong contacting the server. Please try again.',
          timestamp: Date.now() + 1,
          meta:      null,
        },
      ]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const clearChat = () => {
    if (window.confirm('Clear the entire chat history?')) {
      setMessages([getInitialMessage()]);
      localStorage.removeItem(CHAT_STORAGE_KEY);
    }
  };

  return (
    <div className="flex flex-col w-full h-full bg-[#F2F2F7]">

      {/* Header */}
      <div className="bg-[#1A1A1A] px-5 py-2.5 flex-shrink-0 flex items-center justify-between">
        <span className="text-white text-lg font-bold tracking-wide">iICA</span>
        <div className="flex items-center gap-3">
          <button
            onClick={clearChat}
            className="text-gray-400 hover:text-white transition-colors"
            title="Clear chat"
          >
            <Trash2 size={17} />
          </button>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-white transition-colors"
            title="Close"
          >
            <X size={19} />
          </button>
        </div>
      </div>

      {/* Message feed */}
      <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-5 space-y-3">
        <AnimatePresence initial={false}>
          {messages.map((msg) => (
            <MessageBubble key={msg.id} message={msg} />
          ))}
          {isLoading && <TypingIndicator />}
        </AnimatePresence>
      </div>

      {/* Input bar */}
      <div className="bg-white border-t border-gray-200 px-4 py-3 flex items-center gap-3 flex-shrink-0">
        <input
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Type your message..."
          className="flex-1 bg-transparent text-sm text-gray-800 placeholder-gray-400 focus:outline-none"
        />
        <button
          onClick={handleSend}
          disabled={isLoading || !input.trim()}
          className="w-10 h-10 rounded-lg bg-[#1B3F7A] flex items-center justify-center disabled:opacity-40 transition-opacity flex-shrink-0"
        >
          {isLoading
            ? <Loader className="text-white animate-spin" size={16} />
            : (
            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 rotate-90" fill="none" viewBox="0 0 24 24" stroke="white" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
            </svg>
          )}
        </button>
      </div>

    </div>
  );
};

// ---------------------------------------------------------------------------
// Message bubble
// ---------------------------------------------------------------------------

const mdComponents = {
  p:  ({ node, ...props }) => <p className="mb-2 last:mb-0 leading-relaxed" {...props} />,
  ol: ({ node, ...props }) => <ol className="list-decimal list-outside ml-4 mb-2 space-y-1" {...props} />,
  ul: ({ node, ...props }) => <ul className="list-disc list-outside ml-4 mb-2 space-y-1" {...props} />,
  li: ({ node, ...props }) => <li className="text-[13px] leading-relaxed" {...props} />,
  a:  ({ node, href, children, ...props }) => (
    <a href={href} target="_blank" rel="noopener noreferrer"
       className="underline opacity-80 hover:opacity-100" {...props}>
      {children}
    </a>
  ),
  table: ({ node, ...props }) => (
    <div className="overflow-x-auto my-2 rounded-lg border border-gray-200">
      <table className="min-w-full border-collapse text-xs" {...props} />
    </div>
  ),
  thead: ({ node, ...props }) => <thead className="bg-gray-100" {...props} />,
  th:    ({ node, ...props }) => <th className="px-3 py-2 text-left font-semibold text-gray-700 border-b border-gray-200" {...props} />,
  td:    ({ node, ...props }) => <td className="px-3 py-2 border-t border-gray-100 text-gray-800" {...props} />,
  tr:    ({ node, ...props }) => <tr {...props} />,
};

const MessageBubble = ({ message }) => {
  const isUser = message.role === 'user';
  const time   = formatTime(message.timestamp || message.id);

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.25 }}
      className={`flex flex-col ${isUser ? 'items-end' : 'items-start'}`}
    >
      <div
        className={`max-w-[78%] px-4 py-3 rounded-2xl text-sm leading-relaxed ${
          isUser
            ? 'bg-[#2563EB] text-white rounded-br-sm'
            : 'bg-white text-gray-900 shadow-sm rounded-bl-sm'
        }`}
      >
        {isUser ? (
          <span>{message.content}</span>
        ) : (
          <div className="prose prose-sm max-w-none text-gray-900">
            <Markdown remarkPlugins={[remarkGfm]} components={mdComponents}>
              {message.content}
            </Markdown>
          </div>
        )}
      </div>
      <span className={`text-[11px] mt-1 ${isUser ? 'text-gray-400' : 'text-gray-400'}`}>
        {time}
      </span>
    </motion.div>
  );
};

// ---------------------------------------------------------------------------
// Typing indicator
// ---------------------------------------------------------------------------

const TypingIndicator = () => (
  <motion.div
    initial={{ opacity: 0, y: 8 }}
    animate={{ opacity: 1, y: 0 }}
    exit={{ opacity: 0 }}
    className="flex items-start"
  >
    <div className="bg-white shadow-sm rounded-2xl rounded-bl-sm px-4 py-3 flex items-center gap-1.5">
      {[0, 0.18, 0.36].map((delay, i) => (
        <motion.span
          key={i}
          className="w-2 h-2 bg-gray-400 rounded-full block"
          animate={{ opacity: [0.3, 1, 0.3] }}
          transition={{ duration: 1, delay, repeat: Infinity, ease: 'easeInOut' }}
        />
      ))}
    </div>
  </motion.div>
);

export default ChatSection;
